import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { http } from "../lib/api";

const SettingsCtx = createContext(null);

const DEFAULTS = {
  timezone: "Asia/Jakarta",
  locale: "id-ID",
  date_format: "DD-MM-YYYY",
  time_format: "HH:mm",
  datetime_format: "DD-MM-YYYY HH:mm",
  block_logout_with_open_shift: false,
};

const TZ_ABBREV = {
  "Asia/Jakarta": "WIB",
  "Asia/Makassar": "WITA",
  "Asia/Jayapura": "WIT",
};

// Extract localised parts once, then compose based on token format
function parts(date, timezone, locale) {
  const p = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date).reduce((acc, x) => { acc[x.type] = x.value; return acc; }, {});
  // Fix "24" -> "00" for hour edge case in some locales
  if (p.hour === "24") p.hour = "00";
  return p;
}

function monthName(date, timezone, locale, long) {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    month: long ? "long" : "short",
  }).format(date);
}

// Token replacement: YYYY, MM, MMM, MMMM, DD, HH, mm, ss, z (tz abbrev)
function applyFormat(fmt, date, timezone, locale) {
  const p = parts(date, timezone, locale);
  const mmm = monthName(date, timezone, locale, false);
  const mmmm = monthName(date, timezone, locale, true);
  const z = TZ_ABBREV[timezone] || timezone;
  // Replace longer tokens first to avoid partial matches
  return fmt
    .replace(/YYYY/g, p.year)
    .replace(/MMMM/g, mmmm)
    .replace(/MMM/g, mmm)
    .replace(/MM/g, p.month)
    .replace(/DD/g, p.day)
    .replace(/HH/g, p.hour)
    .replace(/mm/g, p.minute)
    .replace(/ss/g, p.second)
    .replace(/\bz\b/g, z);
}

function toDate(iso) {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/settings/board");
      setSettings({
        timezone: data.timezone || DEFAULTS.timezone,
        locale: data.locale || DEFAULTS.locale,
        date_format: data.date_format || DEFAULTS.date_format,
        time_format: data.time_format || DEFAULTS.time_format,
        datetime_format: data.datetime_format || DEFAULTS.datetime_format,
        block_logout_with_open_shift: !!data.block_logout_with_open_shift,
      });
    } catch { /* keep defaults */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const api = useMemo(() => {
    const { timezone, locale, date_format, time_format, datetime_format } = settings;
    const fmt = (iso, pattern) => {
      const d = toDate(iso);
      if (!d) return "-";
      return applyFormat(pattern, d, timezone, locale);
    };
    return {
      settings,
      reloadSettings: load,
      tzAbbrev: TZ_ABBREV[timezone] || timezone,
      fmtDate: (iso) => fmt(iso, date_format),
      fmtTime: (iso) => fmt(iso, time_format),
      fmtDateTime: (iso) => fmt(iso, datetime_format),
      fmtCustom: (iso, pattern) => fmt(iso, pattern),
    };
  }, [settings, load]);

  return <SettingsCtx.Provider value={api}>{children}</SettingsCtx.Provider>;
}

export const useSettings = () => useContext(SettingsCtx) || {
  settings: DEFAULTS,
  tzAbbrev: "WIB",
  fmtDate: (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("-") : "-"),
  fmtTime: (iso) => (iso ? String(iso).slice(11, 16) : "-"),
  fmtDateTime: (iso) => (iso ? `${String(iso).slice(0, 10).split("-").reverse().join("-")} ${String(iso).slice(11, 16)}` : "-"),
  reloadSettings: () => {},
};
